import { useState, useEffect, useRef } from 'react';
import { useModalStore } from '../../../stores/modalStore.js';
import { useFriendStore } from '../../../stores/friendStore.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

const STATUS_COLOR = { online: '#3ba55c', idle: '#faa61a', dnd: '#ed4245', offline: '#747f8d' };

function Avatar({ user }) {
  if (user.avatar_url) {
    return <img src={user.avatar_url} alt={user.username} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />;
  }
  const initial = (user.username || user.display_name || '?')[0].toUpperCase();
  return (
    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
      {user.avatar || initial}
    </div>
  );
}

function UserRow({ user, actions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', marginBottom: 6, border: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <Avatar user={user} />
        <span style={{ position: 'absolute', bottom: 0, right: 0, width: 12, height: 12, borderRadius: '50%', background: STATUS_COLOR[user.status || 'offline'], border: '2px solid #16213e' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.display_name || user.username}
        </div>
        {user.username && user.username !== user.display_name && (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>@{user.username}</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {actions}
      </div>
    </div>
  );
}

const BTN_ACCEPT  = { background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.35)', color: '#34d399' };
const BTN_DECLINE = { background: 'rgba(239,68,68,0.1)',   border: '1px solid rgba(239,68,68,0.3)',   color: '#f87171' };
const BTN_REMOVE  = { background: 'rgba(239,68,68,0.08)',  border: '1px solid rgba(239,68,68,0.2)',   color: '#f87171' };
const BTN_CANCEL  = { background: 'rgba(255,255,255,0.06)',border: '1px solid rgba(255,255,255,0.12)',color: 'rgba(255,255,255,0.5)' };

function Btn({ style, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', ...style }}
    >
      {children}
    </button>
  );
}

const TABS = [
  { id: 'all',      label: 'All Friends' },
  { id: 'online',   label: 'Online' },
  { id: 'pending',  label: 'Pending' },
  { id: 'add',      label: '+ Add Friend' },
];

export function FriendsModal() {
  const { isOpen, type, onClose } = useModalStore();
  const { friends, pendingReceived, pendingSent, loading, fetchAll, sendRequest, acceptRequest, declineRequest, removeFriend } = useFriendStore();

  const [tab, setTab] = useState('all');
  const [addInput, setAddInput] = useState('');
  const [addMsg, setAddMsg] = useState(null);
  const [addLoading, setAddLoading] = useState(false);

  const ref = useRef();
  useFocusTrap(ref, isOpen && type === 'friends');

  useEffect(() => {
    if (isOpen && type === 'friends') {
      fetchAll();
      setTab('all');
      setAddInput('');
      setAddMsg(null);
    }
  }, [isOpen, type]);

  if (!isOpen || type !== 'friends') return null;

  const onlineFriends = friends.filter(f => f.status === 'online');
  const pendingCount = pendingReceived.length + pendingSent.length;

  const handleAdd = async () => {
    if (!addInput.trim()) return;
    setAddLoading(true);
    setAddMsg(null);
    const d = await sendRequest(addInput.trim());
    setAddLoading(false);
    if (d.success) {
      setAddMsg({ ok: true, text: d.accepted ? 'Friend added!' : 'Friend request sent!' });
      setAddInput('');
    } else {
      setAddMsg({ ok: false, text: d.error || 'User not found.' });
    }
  };

  const listForTab = tab === 'all' ? friends : tab === 'online' ? onlineFriends : [];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Friends"
        onClick={e => e.stopPropagation()}
        style={{ background: '#16213e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, width: 520, maxWidth: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ padding: '18px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>👥 Friends</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.07)', marginLeft: -20, marginRight: -20, paddingLeft: 20 }}>
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  color: tab === t.id ? '#a5b4fc' : 'rgba(255,255,255,0.4)',
                  borderBottom: `2px solid ${tab === t.id ? '#6366f1' : 'transparent'}`, marginBottom: -1, whiteSpace: 'nowrap',
                }}
              >
                {t.label}
                {t.id === 'pending' && pendingCount > 0 && (
                  <span style={{ marginLeft: 6, background: '#ef4444', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 5px' }}>{pendingCount}</span>
                )}
                {t.id === 'online' && (
                  <span style={{ marginLeft: 6, background: 'rgba(59,165,92,0.2)', color: '#3ba55c', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 5px' }}>{onlineFriends.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* Add Friend */}
          {tab === 'add' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>Send a friend request by username or user ID.</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={addInput}
                  onChange={e => setAddInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAdd()}
                  placeholder="Enter username or ID…"
                  style={{ flex: 1, padding: '9px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontSize: 14, outline: 'none' }}
                />
                <button
                  onClick={handleAdd}
                  disabled={addLoading || !addInput.trim()}
                  style={{ padding: '9px 18px', borderRadius: 8, background: addLoading ? 'rgba(99,102,241,0.5)' : '#6366f1', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: addLoading ? 'default' : 'pointer' }}
                >
                  {addLoading ? '…' : 'Send'}
                </button>
              </div>
              {addMsg && (
                <div style={{ fontSize: 13, color: addMsg.ok ? '#34d399' : '#f87171', padding: '8px 12px', borderRadius: 7, background: addMsg.ok ? 'rgba(52,211,153,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${addMsg.ok ? 'rgba(52,211,153,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                  {addMsg.text}
                </div>
              )}
            </div>
          )}

          {/* Pending */}
          {tab === 'pending' && (
            <div>
              {pendingReceived.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Incoming — {pendingReceived.length}</div>
                  {pendingReceived.map(u => (
                    <UserRow key={u.id} user={u} actions={[
                      <Btn key="a" style={BTN_ACCEPT}  onClick={() => acceptRequest(u.id)}>✓ Accept</Btn>,
                      <Btn key="d" style={BTN_DECLINE} onClick={() => declineRequest(u.id)}>✕</Btn>,
                    ]} />
                  ))}
                </>
              )}
              {pendingSent.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '14px 0 10px' }}>Sent — {pendingSent.length}</div>
                  {pendingSent.map(u => (
                    <UserRow key={u.id} user={u} actions={[
                      <Btn key="c" style={BTN_CANCEL} onClick={() => declineRequest(u.id)}>Cancel</Btn>,
                    ]} />
                  ))}
                </>
              )}
              {pendingReceived.length === 0 && pendingSent.length === 0 && (
                <div style={{ textAlign: 'center', paddingTop: 40, color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>No pending requests</div>
              )}
            </div>
          )}

          {/* Friends list (All / Online) */}
          {(tab === 'all' || tab === 'online') && (
            loading ? (
              <div style={{ textAlign: 'center', paddingTop: 40, color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>Loading…</div>
            ) : listForTab.length === 0 ? (
              <div style={{ textAlign: 'center', paddingTop: 40, color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>
                {tab === 'online' ? 'No friends online right now.' : 'No friends yet. Add some!'}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                  {tab === 'online' ? 'Online' : 'All'} — {listForTab.length}
                </div>
                {listForTab.map(u => (
                  <UserRow key={u.id} user={u} actions={[
                    <Btn key="r" style={BTN_REMOVE} onClick={() => removeFriend(u.id)}>Remove</Btn>,
                  ]} />
                ))}
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}
